import React, { useCallback, useEffect, useState } from "react";
import { formatCompact } from "../userFloorHelpers";
import { fetchAdminUserTransactions } from "../api/fetchAdminUserTransactions";
import {
  transactionContextLine,
  transactionInitials,
  transactionMetricsLine,
  transactionStatusMeta,
  transactionSummaryParts,
  transactionTypeLabel,
} from "../userTransactionHelpers";
import UserActivitySurface from "./UserActivitySurface.jsx";
import UserSurfaceRow from "./UserSurfaceRow.jsx";
import { SURFACE_PAGE_SIZE } from "./UserSurfacePager.jsx";
import { surfacePageRange } from "./userSurfacePageHelpers";

export default function UserTransactionsTab({ userId }) {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: SURFACE_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ page = 1, refresh = false } = {}) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminUserTransactions(userId, {
          page,
          limit: SURFACE_PAGE_SIZE,
          refresh,
        });
        setTransactions(data?.transactions || []);
        setSummary(data?.statistics?.summary || null);
        const pg = data?.pagination || {};
        setPagination({
          page: pg.currentPage || page,
          limit: pg.transactionsPerPage || SURFACE_PAGE_SIZE,
          total: pg.totalTransactions || 0,
          totalPages: Math.max(1, pg.totalPages || 1),
        });
      } catch (err) {
        setTransactions([]);
        setSummary(null);
        setError(err.response?.data?.error || err.message || "Failed to load transactions");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    load({ page: 1 });
  }, [userId, load]);

  const { page, totalPages, total, limit } = pagination;
  const { startIndex, endIndex } = surfacePageRange(page, limit, transactions.length, total);

  return (
    <UserActivitySurface
      title="Ledger"
      total={summary?.totalTransactions ?? total ?? transactions.length}
      totalLabel="transactions"
      summaryParts={transactionSummaryParts(summary)}
      loading={loading}
      error={error}
      onRefresh={() => load({ page, refresh: true })}
      onRetry={() => load({ page })}
      emptyMessage="No transactions for this member."
      itemCount={transactions.length}
      pager={{
        page,
        totalPages,
        total,
        startIndex,
        endIndex,
        loading,
        onPageChange: (nextPage) => load({ page: nextPage }),
        emptyLabel: "No transactions",
      }}
    >
      {transactions.map((tx, idx) => {
        const status = transactionStatusMeta(tx.status);
        const typeLabel = transactionTypeLabel(tx.transactionType);
        const tags = [{ label: status.label, tone: status.tone }];
        if (tx.isDisputed) tags.push({ label: "Disputed", tone: "warn" });
        return (
          <UserSurfaceRow
            key={tx._id}
            rank={startIndex + idx + 1}
            initials={transactionInitials(tx)}
            name={typeLabel}
            tags={tags}
            context={transactionContextLine(tx)}
            metrics={transactionMetricsLine(tx, formatCompact)}
          />
        );
      })}
    </UserActivitySurface>
  );
}
